/**
 * 统一日志管理模块
 * 生产环境自动屏蔽日志输出
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogConfig {
  enabled: boolean;
  level: LogLevel;
  prefix: string;
}

class Logger {
  private config: LogConfig;
  private readonly isProduction: boolean;

  constructor() {
    // 多重检查确保生产环境检测准确
    this.isProduction = this.checkProductionEnvironment();

    const isLocalhost = typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
       window.location.hostname === '127.0.0.1' ||
       window.location.hostname.includes('localhost'));

    // 生产环境绝对禁用日志，开发环境且本地环境才启用
    const shouldEnable = !this.isProduction && isLocalhost && import.meta.env.DEV;

    this.config = {
      enabled: shouldEnable,
      level: 'debug',
      prefix: '[AutoFill]'
    };

    // 生产环境额外保护：强制禁用
    if (this.isProduction) {
      this.config.enabled = false;
    }

    // 在生产环境中，重写 console 方法以防止意外输出
    if (this.isProduction) {
      this.overrideConsoleInProduction();
    }
  }

  /**
   * 多重检查生产环境
   */
  private checkProductionEnvironment(): boolean {
    // 检查 Vite 环境变量
    if (import.meta.env.PROD === true) return true;
    if (import.meta.env.MODE === 'production') return true;
    
    // 检查 Node 环境变量（构建时）
    try {
      const globalThis = window as any;
      if (globalThis.process?.env?.NODE_ENV === 'production') return true;
    } catch (e) {
      // 忽略 process 不存在的错误
    }
    
    // 检查域名（部署后）
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      // 如果不是本地开发域名，认为是生产环境
      if (hostname !== 'localhost' && 
          hostname !== '127.0.0.1' && 
          !hostname.includes('localhost') &&
          !hostname.includes('192.168.') &&
          !hostname.includes('10.0.') &&
          hostname !== '') {
        return true;
      }
    }
    
    return false;
  }

  /**
   * 在生产环境中重写 console 方法
   */
  private overrideConsoleInProduction(): void {
    if (typeof window === 'undefined') return;

    // 保存原始方法（以防需要恢复）
    const originalConsole = {
      log: console.log,
      info: console.info,
      warn: console.warn,
      error: console.error,
      debug: console.debug,
      trace: console.trace,
      group: console.group,
      groupEnd: console.groupEnd,
      time: console.time,
      timeEnd: console.timeEnd,
    };

    // 创建空函数
    const noop = () => {};

    // 重写 console 方法
    console.log = noop;
    console.info = noop;
    console.warn = noop;
    console.debug = noop;
    console.trace = noop;
    console.group = noop;
    console.groupEnd = noop;
    console.time = noop;
    console.timeEnd = noop;

    // 保留 console.error 用于真正的错误（但限制输出）
    console.error = (...args: any[]) => {
      // 只在真正的错误时输出，且不包含调试信息
      if (args.length > 0 && args[0] instanceof Error) {
        originalConsole.error('Application Error:', args[0].message);
      }
    };

    // 在开发工具中添加提示
    (window as any).__CONSOLE_DISABLED__ = true;
    (window as any).__ORIGINAL_CONSOLE__ = originalConsole;
  }

  /**
   * 设置日志配置
   */
  configure(config: Partial<LogConfig>): void {
    // 生产环境强制禁用，不允许通过配置启用
    const isProduction = import.meta.env.PROD || import.meta.env.MODE === 'production';
    if (isProduction) {
      this.config = { ...this.config, ...config, enabled: false };
    } else {
      this.config = { ...this.config, ...config };
    }
  }

  /**
   * 强制禁用所有日志（用于生产环境部署）
   */
  forceDisable(): void {
    this.config.enabled = false;
  }

  /**
   * 检查日志是否启用
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * 格式化日志消息
   */
  private formatMessage(level: LogLevel, message: string, data?: any): string {
    const timestamp = new Date().toLocaleTimeString();
    const levelIcon = this.getLevelIcon(level);
    return `${this.config.prefix} ${levelIcon} [${timestamp}] ${message}`;
  }

  /**
   * 获取日志级别图标
   */
  private getLevelIcon(level: LogLevel): string {
    const icons = {
      debug: '🔧',
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌'
    };
    return icons[level];
  }

  /**
   * 检查是否应该输出日志
   */
  private shouldLog(level: LogLevel): boolean {
    // 生产环境强制检查
    const isProduction = import.meta.env.PROD ||
                        import.meta.env.MODE === 'production';

    if (isProduction) return false;
    if (!this.config.enabled) return false;

    const levels = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const messageLevelIndex = levels.indexOf(level);

    return messageLevelIndex >= currentLevelIndex;
  }

  /**
   * 生产环境检查
   */
  private isProductionEnvironment(): boolean {
    return import.meta.env.PROD ||
           import.meta.env.MODE === 'production';
  }

  /**
   * 调试日志
   */
  debug(message: string, data?: any): void {
    if (this.isProductionEnvironment()) return;
    if (!this.shouldLog('debug')) return;

    const formattedMessage = this.formatMessage('debug', message);
    if (data !== undefined) {
      console.log(formattedMessage, data);
    } else {
      console.log(formattedMessage);
    }
  }

  /**
   * 信息日志
   */
  info(message: string, data?: any): void {
    if (this.isProductionEnvironment()) return;
    if (!this.shouldLog('info')) return;

    const formattedMessage = this.formatMessage('info', message);
    if (data !== undefined) {
      console.info(formattedMessage, data);
    } else {
      console.info(formattedMessage);
    }
  }

  /**
   * 警告日志
   */
  warn(message: string, data?: any): void {
    if (this.isProductionEnvironment()) return;
    if (!this.shouldLog('warn')) return;

    const formattedMessage = this.formatMessage('warn', message);
    if (data !== undefined) {
      console.warn(formattedMessage, data);
    } else {
      console.warn(formattedMessage);
    }
  }

  /**
   * 错误日志
   */
  error(message: string, error?: any): void {
    if (this.isProductionEnvironment()) return;
    if (!this.shouldLog('error')) return;

    const formattedMessage = this.formatMessage('error', message);
    if (error !== undefined) {
      console.error(formattedMessage, error);
    } else {
      console.error(formattedMessage);
    }
  }

  /**
   * 分组日志开始
   */
  group(label: string): void {
    if (this.isProductionEnvironment()) return;
    if (!this.config.enabled) return;
    console.group(`${this.config.prefix} 📋 ${label}`);
  }

  /**
   * 分组日志结束
   */
  groupEnd(): void {
    if (this.isProductionEnvironment()) return;
    if (!this.config.enabled) return;
    console.groupEnd();
  }

  /**
   * 性能计时开始
   */
  time(label: string): void {
    if (this.isProductionEnvironment()) return;
    if (!this.config.enabled) return;
    console.time(`${this.config.prefix} ⏱️ ${label}`);
  }

  /**
   * 性能计时结束
   */
  timeEnd(label: string): void {
    if (this.isProductionEnvironment()) return;
    if (!this.config.enabled) return;
    console.timeEnd(`${this.config.prefix} ⏱️ ${label}`);
  }
}

// 创建全局日志实例
export const logger = new Logger();

// 导出便捷方法
export const log = {
  debug: (message: string, data?: any) => logger.debug(message, data),
  info: (message: string, data?: any) => logger.info(message, data),
  warn: (message: string, data?: any) => logger.warn(message, data),
  error: (message: string, error?: any) => logger.error(message, error),
  group: (label: string) => logger.group(label),
  groupEnd: () => logger.groupEnd(),
  time: (label: string) => logger.time(label),
  timeEnd: (label: string) => logger.timeEnd(label)
};

// 默认导出
export default logger;